import type { ClientConfig } from '@liniya/api-client';
import { attachmentKind } from '@liniya/api-client';
import { notify, useComposerStore } from '@liniya/client-core';
import { useCallback } from 'react';

import type { TransferMedia } from './media-transfer';

/** Held only until the server's own figure arrives with the configuration. */
const FALLBACK_MAXIMUM_BYTES = 25 * 1024 * 1024;

export type AttachMedia = (media: TransferMedia[]) => void;

/**
 * Putting a chosen file into a chat's composer, however it was chosen.
 *
 * The paperclip, a paste and a drop differ only in where the file came from.
 * Everything after that is the same question three times — what the server will
 * accept, what preview to build, which previous preview to release, and the
 * fact that one message carries one attachment — and answering it three times
 * is how the three routes would grow three different limits.
 */
export function useAttachFile(
  chatId: string,
  limits: ClientConfig['limits'] | undefined,
): AttachMedia {
  const setAttachment = useComposerStore((state) => state.setAttachment);
  // The limit comes from the server rather than a constant in this file, so the
  // refusal can never disagree with what would actually have been accepted.
  const maximum = limits?.max_attachment_bytes ?? FALLBACK_MAXIMUM_BYTES;

  return useCallback(
    (media) => {
      const [first, ...rest] = media;
      if (!first) return;

      if (first.file.size > maximum) {
        notify(`Файл больше допустимых ${Math.floor(maximum / (1024 * 1024))} МБ.`, 'error');
        return;
      }

      setAttachment(chatId, {
        file: first.file,
        name: first.name,
        size: first.file.size,
        previewUrl: URL.createObjectURL(first.file),
        kind: attachmentKind(first.name),
      });

      // Said rather than silently ignored: somebody who selected five files
      // otherwise has no way to know that four of them were dropped.
      if (rest.length > 0) {
        notify('В сообщение помещается одно вложение — прикреплён первый файл.');
      }
    },
    [chatId, maximum, setAttachment],
  );
}
